import { useState, useRef, useCallback } from 'react'
import { toast } from 'sonner'

// ─── Tipo base compartilhado ──────────────────────────────
export interface NotaItem {
  id: string | number
  tipo: string
  conteudo?: string | null
  signedUrl?: string | null
  legenda?: string | null
  legenda_audio_path?: string | null
  criado_em: string
  _uploading?: boolean
  _error?: string | null
  duracao_segundos?: number | null
  storage_path?: string | null
}

// ─── RelTime ──────────────────────────────────────────────
interface RelTimeProps { iso: string }

export function RelTime({ iso }: RelTimeProps) {
  const d = new Date(iso)
  return (
    <span className="text-[10px] text-[#555]">
      {d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
    </span>
  )
}

// ─── DeleteBtn ────────────────────────────────────────────
interface DeleteBtnProps { onDelete: () => void }

export function DeleteBtn({ onDelete }: DeleteBtnProps) {
  return (
    <button
      onClick={onDelete}
      className="w-6 h-6 flex items-center justify-center rounded-full shrink-0 border-none cursor-pointer transition-colors hover:bg-red-900/30"
      style={{ background: 'transparent', color: '#555' }}
      title="Remover"
    >
      <span className="material-symbols-outlined text-sm">delete</span>
    </button>
  )
}

// ─── MicButton ────────────────────────────────────────────
interface MicButtonProps {
  notaId: string | number
  addLegendaVoz: (notaId: string | number, blob: Blob) => Promise<void>
  hasLegenda: boolean
}

export function MicButton({ notaId, addLegendaVoz, hasLegenda }: MicButtonProps) {
  const [recording, setRecording] = useState<boolean>(false)
  const recRef    = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])

  const toggleRecord = useCallback(async () => {
    if (recording) { recRef.current?.stop(); return }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      chunksRef.current = []
      const rec = new MediaRecorder(stream)
      recRef.current = rec
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        await addLegendaVoz(notaId, blob)
        setRecording(false)
      }
      rec.start()
      setRecording(true)
    } catch (err) {
      if ((err as { name?: string })?.name === 'NotAllowedError' || (err as { name?: string })?.name === 'PermissionDeniedError') {
        toast.error('Permissão de microfone negada. Habilite nas configurações do navegador.')
      }
    }
  }, [recording, notaId, addLegendaVoz])

  return (
    <button
      onClick={toggleRecord}
      className="w-6 h-6 flex items-center justify-center rounded-full shrink-0 border-none cursor-pointer transition-colors"
      style={{
        background: recording ? 'rgba(217,48,37,0.20)' : (hasLegenda ? 'rgba(79,127,114,0.15)' : 'rgba(255,255,255,0.05)'),
        color: recording ? 'var(--color-vermelho)' : (hasLegenda ? 'var(--color-verde)' : '#555'),
      }}
      title={recording ? 'Parar' : (hasLegenda ? 'Regravar legenda' : 'Gravar legenda de voz')}
    >
      <span className="material-symbols-outlined text-[13px]">{recording ? 'stop' : 'mic'}</span>
    </button>
  )
}

// ─── EditableLegenda ──────────────────────────────────────
interface EditableLegendaProps {
  nota: NotaItem
  updateNota: (id: string | number, data: Record<string, unknown>) => void
}

export function EditableLegenda({ nota, updateNota }: EditableLegendaProps) {
  const [editing, setEditing] = useState<boolean>(false)
  const [text,    setText]    = useState<string>(nota.legenda ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  const save = useCallback(() => {
    setEditing(false)
    const trimmed = text.trim()
    if (trimmed !== (nota.legenda ?? '')) {
      updateNota(nota.id, { legenda: trimmed || null })
    }
  }, [text, nota.id, nota.legenda, updateNota])

  if (!editing) {
    return (
      <button
        onClick={() => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 50) }}
        className="text-[12px] leading-snug border-none bg-transparent cursor-pointer p-0 text-left"
        style={{ color: nota.legenda ? '#aaa' : '#555' }}
      >
        {nota.legenda || '+ legenda'}
      </button>
    )
  }

  return (
    <input
      ref={inputRef}
      autoFocus
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={save}
      onKeyDown={e => e.key === 'Enter' && save()}
      placeholder="Legenda..."
      className="w-full text-[12px] text-[#ddd] bg-white/[0.06] rounded-lg px-2 py-1 border border-white/[0.10] outline-none placeholder:text-[#444]"
    />
  )
}
